import Scroll from 'react-scroll';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  formatDirectUrl,
  isArticleTab,
  isSpecial,
  isUngatedVideo,
  PAGE_TYPE_CREDITS,
  PANEL_RECOMMENDED_SERIES,
  PANEL_TYPE_PROMOS,
  SERIES_TYPE_SERIES_COLLECTION,
  slugify,
  urlIsExternal,
} from 'helpers/ContentHelper';
import { getArticleApiId } from 'components/Article/ArticleHelpers';

import { helperGetShowUrl } from './RouteHelper';
import Regex from './Regex';
import { isHiddenForAnonymous } from './AnonymousHelper';

const PANEL_TYPE_EPISODES = 'Season';
export const PANEL_TYPE_CLIPS = 'VideoList';
const PANEL_TYPE_PERSON_BRANDING = 'personBranding';
const PANEL_TYPE_CAST = 'Cast';
const PANEL_TYPE_SERIES = 'Series';

export const PROMO_TYPE_FEATURED_TEXT = 'featuredText';
export const PROMO_TYPE_LEAD = 'lead';
export const PROMO_TYPE_INSET = 'inset';
export const PROMO_TYPE_MARKETING_VIDEO = 'marketingVideoBanner';
const PROMO_TYPE_NAV_ITEM = 'nav-item';
const PROMO_TYPE_CALENDAR = 'calendar';
const PROMO_TYPE_GALLERY = 'gallery';
export const PROMO_TYPE_HERO = 'hero';
export const PROMO_MARKETING_PERKS = 'marketing-perks';

export const NO_TAB_STATE_TRAILER = 'NO_TAB_STATE_TRAILER';

const GROUPED_PANELS = [PANEL_TYPE_EPISODES, PANEL_TYPE_SERIES];

const enableBioLanding = true;

const isTabGrouped = ({ tabType, seriesType }) => {
  if (seriesType === SERIES_TYPE_SERIES_COLLECTION || isSpecial(seriesType)) return false;
  return GROUPED_PANELS.indexOf(tabType) >= 0;
};

export const getTabName = ({ tab, seriesType }) => {
  if (!tab) return null;
  const {
    '@type': type,
    seasonNumber,
    name,
    ctaText,
    headline,
    displaySeasonAndEpisodeCounts,
  } = tab;
  const {
    seriesDetail_seasonLabel: seasonLabel,
    seriesDetail_castHeader: creditsLabel,
  } = window.Language;

  if (type === PANEL_TYPE_CAST) return creditsLabel;
  if (seriesType === SERIES_TYPE_SERIES_COLLECTION) return name;
  if (type === PANEL_TYPE_EPISODES && headline && !displaySeasonAndEpisodeCounts) return headline;
  if (type === PANEL_TYPE_EPISODES && !headline) return `${seasonLabel} ${seasonNumber}`;
  return headline || ctaText || name;
};

export const getTabSlug = ({ tab, tabs, noTabHomepage, seriesType, isSubmenu }) => {
  if (!tab) return null;
  const { '@type': type } = tab;
  const headline = getTabName({ tab, seriesType })
    .toString()
    .toLowerCase()
    .replace(Regex.SPACES, '-') // Replace spaces with -
    .replace(Regex.NON_WORD_CHARACTERS, '') // Remove all non-word chars
    .replace(Regex.MULTIPLE_DASHES, '-') // Replace multiple - with single -
    .replace(Regex.LEADING_DASH, '') // Trim - from start of text
    .replace(Regex.TRAILING_DASH, ''); // Trim - from end of text
  const directUrl = _.get(tab, 'url');
  if (directUrl) {
    const firstDirectUrl = _.get(tabs, '0.url');
    if (directUrl && !urlIsExternal(directUrl) && firstDirectUrl === directUrl && !isSubmenu)
      return '';
  }
  if (tab && tabs) {
    if (!noTabHomepage && tabs.indexOf(tab) === 0) {
      return '';
    }
    const tabType = _.get(tab, '@type', null);
    const firstTabType = _.get(tabs, '0.@type', null);
    if (tabType === firstTabType && isTabGrouped({ tabType, seriesType })) {
      return tabType === PANEL_TYPE_EPISODES ? headline : '';
    }
  }
  if (type === PANEL_TYPE_CLIPS) return 'clips';
  if (type === PANEL_TYPE_EPISODES) return 'episodes';
  if (type === PANEL_TYPE_CAST) return 'credits';
  if (!headline) return null;
  return headline;
};

const getTabPageId = ({ noTabHomepage, tab, tabs }) => {
  const { '@type': type, name, filter } = tab;
  if (!noTabHomepage && tabs.indexOf(tab) === 0) return '';
  if (type === PANEL_TYPE_CLIPS && filter) return `/${_.kebabCase(name)}`;
  return '';
};

const getPersonalityTabSlug = ({ tab, tabs, noTabHomepage, seriesType }) => {
  if (!tab) return null;
  const { '@type': type, seasonNumber } = tab;
  const headline = getTabName({ tab, seriesType });

  if (tab && tabs) {
    if (!noTabHomepage && tabs.indexOf(tab) === 0) {
      return '';
    }
    const tabType = _.get(tab, '@type', null);
    const firstTabType = _.get(tabs, '0.@type', null);
    if (tabType === firstTabType && isTabGrouped({ tabType, seriesType })) {
      return tabType === PANEL_TYPE_EPISODES ? `season-${seasonNumber}` : '';
    }
  }
  if (type === PANEL_TYPE_CLIPS) return 'clips';
  if (type === PANEL_TYPE_EPISODES) return 'episodes';
  if (type === PANEL_TYPE_CAST) return 'credits';
  if (!headline) return null;
  return headline
    .toString()
    .toLowerCase()
    .replace(Regex.SPACES, '-') // Replace spaces with -
    .replace(Regex.NON_WORD_CHARACTERS, '') // Remove all non-word chars
    .replace(Regex.MULTIPLE_DASHES, '-') // Replace multiple - with single -
    .replace(Regex.LEADING_DASH, '') // Trim - from start of text
    .replace(Regex.TRAILING_DASH, ''); // Trim - from end of text;
};

export const getTabUrl = ({ showId, tab, tabs, noTabHomepage, seriesType, isSubmenu }) => {
  const directUrl = _.get(tab, 'url');
  if (directUrl && urlIsExternal(directUrl)) return formatDirectUrl(directUrl);
  const tabSlug = getTabSlug({ tab, tabs, noTabHomepage, seriesType, isSubmenu });
  const tabPageId = getTabPageId({ noTabHomepage, tab, tabs });
  let url = helperGetShowUrl(showId);
  if (tabSlug) url = `${url}${tabSlug}${tabPageId}/`;
  return url;
};

const isTabSelected = ({
  isPersonality,
  tab,
  tabs,
  noTabState,
  noTabHomepage,
  seriesType,
  pageId,
}) => {
  if (!tab) return false;
  const tabSlug = isPersonality
    ? getPersonalityTabSlug({ tab, tabs, noTabHomepage, seriesType })
    : getTabSlug({ tab, tabs, noTabHomepage, seriesType });
  const tabPageId = getTabPageId({ noTabHomepage, tab, tabs });
  if (pageId === tabSlug) return true;
  if (tabSlug === PAGE_TYPE_CREDITS && _.includes(pageId, tabSlug)) return true;
  if (pageId === `${tabSlug}${tabPageId}`) return true;
  if (noTabState) return false;
  if (!pageId) return true;
  return false;
};

export const hasChildren = tab => {
  const children = _.get(tab, 'children', null);
  return children && children.length > 0;
};

export const getSelectedTabIndex = ({
  isPersonality,
  pageId,
  tabs,
  noTabState,
  noTabHomepage,
  seriesType,
}) => {
  let subTabIndex = false;
  const tabIndex = tabs.findIndex(tab => {
    let selected = false;
    selected = isTabSelected({
      pageId,
      tab,
      tabs,
      noTabState,
      noTabHomepage,
      seriesType,
      isPersonality,
    });
    if (!selected && hasChildren(tab)) {
      const childTabs = _.get(tab, 'children');
      const { tabIndex: childIndex } = getSelectedTabIndex({
        pageId,
        tabs: childTabs,
        noTabState,
        noTabHomepage,
        seriesType,
      });
      if (childIndex !== false) {
        subTabIndex = childIndex;
        selected = true;
      }
    }
    return selected;
  });
  if (tabIndex < 0) {
    return false;
  }
  return { tabIndex, subTabIndex };
};

export const getPromoData = ({ data, promoType }) =>
  _.find(data, promo => {
    if (promoType === PROMO_MARKETING_PERKS) {
      const collectionType = _.get(promo, 'collectionType', '');
      return collectionType === PROMO_MARKETING_PERKS;
    }
    const type = _.get(promo, 'items.member[0].promoType', '');
    return type === promoType;
  });

export function clearFetchNodeInterval() {
  const { currentFetchNode } = this.state;
  const currentFetchInterval = _.get(currentFetchNode, 'fetch');
  if (currentFetchNode) {
    clearInterval(currentFetchInterval);
    this.setState({ currentFetchNode: null });
  }
}

export function checkForDOMNode(nextNodeId, duration, offsetTop) {
  const { currentFetchNode } = this.state;
  const currentNodeId = _.get(currentFetchNode, 'id');
  const currentFetchInterval = _.get(currentFetchNode, 'fetch');
  const nodeIdsMatch = currentNodeId === nextNodeId;
  if (!nodeIdsMatch) {
    if (currentFetchNode) {
      clearInterval(currentFetchInterval);
      this.setState({ currentFetchNode: null });
    }
    let count = 0;
    const fetchNodeInterval = setInterval(() => {
      count += 1;
      const node = document.getElementById(nextNodeId);
      if (node) {
        const nodeTop = node.offsetTop;
        Scroll.animateScroll.scrollTo(nodeTop + offsetTop);
        clearInterval(fetchNodeInterval);
        this.setState({ currentFetchNode: null });
      }

      const isExpired = count === duration;
      if (isExpired) {
        clearInterval(currentFetchInterval);
        this.setState({ currentFetchNode: null });
      }
    }, 1000);
    this.setState({ currentFetchNode: { id: nextNodeId, fetch: fetchNodeInterval } });
  }
}

export const scrollToElement = ({ slug, nodeFetcher, duration, clearNodeFetcher, offsetTop }) => {
  const elementId = slug;
  if (duration && nodeFetcher) return nodeFetcher(elementId, duration, offsetTop);

  const node = document.getElementById(elementId);
  if (!node) return null;
  clearNodeFetcher();
  const nodeTop = node.offsetTop;
  return Scroll.animateScroll.scrollTo(nodeTop + offsetTop);
};

export const hasPrePremiereVideo = item =>
  _.get(item, '0.items.member.0.previewVideo.@populated', false);

export const isInlineComponent = elem => elem && elem.panelType === 'showBranding';
export const isContinueWatchingComponent = elem => elem && elem.panelType === 'continueWatching';
const isVideoComponent = elem => elem && elem.panelType === 'videos';
const isPromoItem = elem => elem && elem['@type'] === 'Promo';

const isValidPromoItem = elem =>
  elem &&
  elem['@type'] === 'Promo' &&
  (elem.promoType === PROMO_TYPE_NAV_ITEM || elem.promoType === PROMO_TYPE_GALLERY) &&
  (elem.url || hasChildren(elem));

export const getAnonymousCTAText = ({ item }) => {
  const {
    seriesDetailAnonymous_promoUngatedCTA,
    seriesDetailAnonymous_promoGatedCTA,
  } = window.Language;
  return isUngatedVideo(item)
    ? seriesDetailAnonymous_promoUngatedCTA
    : seriesDetailAnonymous_promoGatedCTA;
};

export const getTabDataUrl = item => {
  const videos = _.get(item, 'items.member', null);
  if (videos) return null;
  let url = _.get(item, 'episodes.@id', null);
  if (!url) url = _.get(item, 'items.@id', null);
  return url;
};

export const isSeasonPanel = panel => _.get(panel, '@type') === PANEL_TYPE_EPISODES;

const isAboutSection = section =>
  _.get(section, 'siteScanUrl', '')
    .split('/')
    .pop() === 'about-the-show';

const getSectionOrder = sections => {
  const sectionOrder = [];
  sections.forEach(section => {
    const type = _.get(section, '@type', '').toLowerCase();
    const promoType = _.get(section, 'promoType', '').toLowerCase();
    const isAbout = isAboutSection(section);
    const isClips = type === 'videolist' || type === 'screenpanel';
    const isHowTo = promoType === 'how-to';
    const isUniq = type =>
      type && !_.find(sectionOrder, ({ type: sectionType }) => sectionType === type) && !isHowTo;
    if (!isClips && isUniq(type)) sectionOrder.push({ section, type });
    if (isClips) sectionOrder.push({ section, type: 'clips' });
    if (isAbout && isUniq('about')) sectionOrder.push({ section, type: 'about' });
  });
  return sectionOrder;
};
const buildArticle = item => {
  const isArticle = isArticleTab(item);
  const articleId = isArticle ? getArticleApiId(item.url) : null;
  return {
    ...item,
    isArticle,
    articleId,
  };
};

const getLinks = sections => {
  const aboutLink = _.filter(sections, section => isAboutSection(section)).map(item =>
    buildArticle(item),
  );
  const links = _.filter(
    sections,
    section => isValidPromoItem(section) && !isAboutSection(section),
  ).map(item => buildArticle(item));
  return aboutLink ? [...links, ...aboutLink] : links;
};

const transformFaq = (faqs = []) => faqs?.map(item => ({ ...item, id: uuidv4() }));

export const getDetailData = ({
  data,
  inlineComponent,
  latestEpisode,
  panels,
  screenTypeId,
  seriesData,
}) => {
  const oldestEpisode = _.get(seriesData, 'oldestEpisode');
  const promos = _.filter(panels, { panelType: PANEL_TYPE_PROMOS });
  const calendar = _.find(_.get(promos, 'items.member'), { promoType: PROMO_TYPE_CALENDAR });
  const latestEpisodeBadges = _.get(latestEpisode, 'contentLabel');
  const isLatestEpisodeLive =
    latestEpisodeBadges && latestEpisodeBadges.find(badge => badge.toLowerCase() === 'live');
  const videoComponents = panels.filter(elem => isVideoComponent(elem));
  const continueWatchingComponents = panels.filter(elem => isContinueWatchingComponent(elem));
  const continueWatchingItems =
    continueWatchingComponents.length > 0
      ? _.get(continueWatchingComponents, '0.items.member')
      : [];
  const continueWatching = continueWatchingItems.slice(0, 1);
  const seo = _.get(data, 'SEO', null);
  const isPersonality = _.get(data, 'categoryType') === 'personality';
  let seriesType;
  let socials = null;
  let faq = null;

  switch (screenTypeId) {
    case 'special-detail':
      seriesType = 'special';
      break;
    case 'movie-detail':
      seriesType = 'movie';
      break;
    case 'series-detail':
    default:
      seriesType = 'show';
  }
  const bio =
    panels.filter(section => section?.panelType === PANEL_TYPE_PERSON_BRANDING)[0]?.items
      ?.member[0] ?? null;
  if (bio) {
    faq = transformFaq(bio?.faq ?? []);
    socials = {
      twitter: bio?.twitterId ?? null,
      facebook: bio?.facebookId ?? null,
      instagram: bio?.instagramId ?? null,
    };
  }
  let favoritableItems = null;
  if (isPersonality) {
    favoritableItems = _.get(data, 'favoritableItems');
  } else {
    const showBrandingPanel = _.find(panels, isInlineComponent);
    favoritableItems = _.get(showBrandingPanel, 'items.member.0.favoritableItems');
  }
  let tabComponents = panels.filter(
    elem =>
      !isInlineComponent(elem) &&
      !isContinueWatchingComponent(elem) &&
      !isVideoComponent(elem) &&
      !isHiddenForAnonymous(elem),
  );
  if (!isPersonality) {
    tabComponents = _.flatten(tabComponents.map(item => _.get(item, 'items.member')));
  }
  const recommendedComponents = _.filter(
    panels,
    tab => tab?.headline?.toLowerCase() === PANEL_RECOMMENDED_SERIES,
  );
  if (recommendedComponents.length) {
    tabComponents.push(recommendedComponents[0]);
  }
  const nonPromoComponents = _.filter(tabComponents, tab => !isPromoItem(tab));
  let promoComponents = _.filter(tabComponents, tab => isValidPromoItem(tab));
  promoComponents = _.sortBy(promoComponents, ['featuredWeight']);
  tabComponents = _.concat(nonPromoComponents, promoComponents);
  tabComponents = _.concat(videoComponents, tabComponents);

  const credits = _.get(inlineComponent, 'items.member.0.credits');
  if (credits && enableBioLanding) {
    credits.id = 'pseudo-tab-credits';
    credits['@type'] = PANEL_TYPE_CAST;
    // @todo: set the correct image filter when we have the correct data from the api.
    const castMembersWithImages = _.get(credits, 'member', []).filter(
      item => !!_.get(item, 'images'),
    );
    credits.member = castMembersWithImages;
    if (credits.member.length > 0) {
      tabComponents.push(credits);
    }
  }
  const links = getLinks(tabComponents);

  const seriesSortOrder = getSectionOrder(tabComponents);
  return {
    calendar,
    continueWatching,
    continueWatchingItems,
    faq,
    favoritableItems,
    isLatestEpisodeLive,
    links,
    oldestEpisode,
    promos,
    seo,
    seriesSortOrder,
    seriesType,
    socials,
    tabComponents,
  };
};

export const getTabsOfMultipleTypes = (tabs = [], types = []) =>
  tabs.filter(tab => {
    const { '@type': tabType } = tab;
    return _.includes(types, tabType);
  });

export const getSelectedItem = tabs => {
  const path = window.location.pathname;
  const selectedItem = _.find(tabs, tab => {
    const urlMatches = path.includes(tab.url);
    const slugMatches = path.includes(slugify(tab.name));
    return urlMatches || slugMatches;
  });

  return selectedItem || tabs[0];
};
